/**
 * `/` — homepage.
 *
 * Single hero on flat Ink. Pixel-faithful port of
 * `design/ui_kits/web/index.html`.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Wordmark } from '../components/brand/Wordmark';
import { useSession } from '../hooks/useSession';

export function HomePage() {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by DemoPage when a signed-in user clicks a demo link — we redirect
  // them here rather than silently destroy their session.
  const alreadySignedInBanner = Boolean(
    (location.state as { alreadySignedInBanner?: boolean } | null)?.alreadySignedInBanner
  );
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--ink)',
        color: 'var(--paper)',
        fontFamily: 'var(--font-sans)',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: 'url(/design/assets/pattern-dots.svg)',
        backgroundSize: '200px 200px',
        backgroundBlendMode: 'overlay',
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          padding: '28px 32px 24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Wordmark tone="paper" size={20} />
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13 }}>
              <span style={{ color: 'rgba(237,237,242,0.7)' }}>
                {user.displayName}
                {user.handle && (
                  <span style={{ color: 'rgba(237,237,242,0.45)', marginLeft: 6 }}>
                    @{user.handle}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={async () => {
                  await logout();
                  navigate('/');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(237,237,242,0.7)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                Sign out
              </button>
            </div>
          ) : (
            <Link
              to="/sign-in"
              style={{
                fontSize: 13,
                color: 'rgba(237,237,242,0.7)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Sign in
            </Link>
          )}
        </div>

        <main
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '40px 16px',
            gap: 28,
          }}
        >
          {alreadySignedInBanner && (
            <div
              role="alert"
              data-testid="already-signed-in-banner"
              style={{
                background: 'rgba(245,120,24,0.12)',
                color: 'var(--ember)',
                border: '1px solid rgba(245,120,24,0.32)',
                borderRadius: 6,
                padding: '10px 16px',
                fontSize: 13,
                fontFamily: 'var(--font-sans)',
                maxWidth: 520,
              }}
            >
              You're already signed in. Sign out first to open a demo session.
            </div>
          )}
          <BetaPill />
          <h1
            style={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontWeight: 900,
              fontSize: 'clamp(56px, 11vw, 144px)',
              letterSpacing: '-0.04em',
              lineHeight: 0.92,
              color: 'var(--paper)',
              margin: 0,
            }}
          >
            Argue freely.
            <br />
            <span style={{ color: 'var(--ember)', fontStyle: 'italic', fontWeight: 900 }}>Verify</span>{' '}
            relentlessly.
          </h1>
          <p
            style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 'clamp(22px, 2.6vw, 28px)',
              color: 'rgba(237,237,242,0.78)',
              maxWidth: 560,
              lineHeight: 1.35,
              margin: 0,
            }}
          >
            Automated evidence integrity verification for national circuit debate. Now in alpha.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <Link
              to={user ? '/my-documents' : '/waitlist'}
              style={{
                fontFamily: 'var(--font-sans)',
                background: 'var(--ember)',
                color: 'var(--ember-ink)',
                border: 0,
                borderRadius: 4,
                padding: '16px 28px',
                fontSize: 15,
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                boxShadow:
                  '0 1px 0 rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.18)',
              }}
            >
              {user ? 'Launch Eloquor' : 'Join the waitlist'} <span aria-hidden>→</span>
            </Link>
            <Link
              to="/invite"
              style={{
                fontSize: 13.5,
                color: 'rgba(237,237,242,0.62)',
                textDecoration: 'none',
              }}
            >
              Have an invite code?{' '}
              <u style={{ textDecorationColor: 'rgba(237,237,242,0.4)', textUnderlineOffset: 3 }}>
                Redeem it
              </u>
            </Link>
          </div>
        </main>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'end',
            fontSize: 11.5,
            color: 'rgba(237,237,242,0.48)',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.04em',
          }}
        >
          <span>ELOQUOR · MMXXVI</span>
          <span
            style={{
              fontStyle: 'italic',
              fontFamily: 'var(--font-serif)',
              letterSpacing: 0,
              fontSize: 13,
              color: 'rgba(237,237,242,0.55)',
            }}
          >
            A Raymond Xu Production
          </span>
        </div>
      </div>
    </div>
  );
}

function BetaPill() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 12px 5px 9px',
        background: 'rgba(245,120,24,0.12)',
        color: 'var(--ember)',
        border: '1px solid rgba(245,120,24,0.32)',
        borderRadius: 999,
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: 'var(--ember)',
          boxShadow: '0 0 0 0 rgba(245,120,24,0.7)',
        }}
      />
      closed alpha · by invite
    </span>
  );
}
